import React, { useState, useEffect } from 'react';
import { api } from '../utils/api';
import { toast } from 'sonner';
import { Users, Gem, Plus, Edit, Trash2, Check, X, Upload, Ban, CheckCircle } from 'lucide-react';
import { DiamondLoader } from '../components/DiamondLoader';

export const AdminDashboard = () => {
  var IMGURL = "https://justinndiam.com:8000";
  const [activeTab, setActiveTab] = useState('users');
  const [pendingUsers, setPendingUsers] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [diamonds, setDiamonds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDiamondForm, setShowDiamondForm] = useState(false);
  const [editingDiamond, setEditingDiamond] = useState(null);
  const [diamondForm, setDiamondForm] = useState({
    shape: '',
    size: '',
    colour: '',
    clarity: '',
    measurement: '',
    fluorescence: '',
    certificate: '',
    lab: '',
    cut: '',
    polish: '',
    symmetry: '',
    comment: '',
    no_bgm: false,
    eye_clean: false,
    location: '',
    rap_discount: '',
    price_per_carat: '',
    total_amount: '',
    visibility: 'public',
    ratio: '',
    stock_id: '',
    shade: '',
    images: [],
    videos: [],
  });

  useEffect(() => {
    fetchData();
  }, [activeTab]);

  const fetchData = async () => {
    setLoading(true);
    try {
      if (activeTab === 'users') {
        const [pending, all] = await Promise.all([
          api.getPendingUsers(),
          api.getAllUsers()
        ]);
        setPendingUsers(pending.data);
        setAllUsers(all.data);
      } else {
        const response = await api.getDiamonds();
        setDiamonds(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleApproveUser = async (userId) => {
    try {
      await api.approveUser(userId);
      toast.success('User approved successfully');
      fetchData();
    } catch (error) {
      toast.error('Failed to approve user');
    }
  };

  const handleRejectUser = async (userId) => {
    try {
      await api.rejectUser(userId);
      toast.success('User rejected successfully');
      fetchData();
    } catch (error) {
      toast.error('Failed to reject user');
    }
  };

  const handleBlockUser = async (userId) => {
    try {
      await api.updateUserStatus(userId, 'suspended');
      toast.success('User blocked successfully');
      fetchData();
    } catch (error) {
      toast.error('Failed to block user');
    }
  };

  const handleUnblockUser = async (userId) => {
    try {
      await api.updateUserStatus(userId, 'approved');
      toast.success('User unblocked successfully');
      fetchData();
    } catch (error) {
      toast.error('Failed to unblock user');
    }
  };

  const handleMediaUpload = async (e) => {
    console.log("upload image called");
    const files = Array.from(e.target.files);
    console.log("upload image called files",files);
    const uploadPromises = files.map(async (file) => {
      try {
        const response = await api.uploadImage(file);
        return {
          url: response.data.url,
          type: response.data.type
        };
      } catch (error) {
        toast.error(`Failed to upload ${file.name}`);
        return null;
      }
    });

    const results = await Promise.all(uploadPromises);
    const validResults = results.filter(r => r !== null);
    
    validResults.forEach(result => {
      if (result.type === 'video') {
        setDiamondForm(prev => ({
          ...prev,
          videos: [...prev.videos, result.url]
        }));
      } else {
        setDiamondForm(prev => ({
          ...prev,
          images: [...prev.images, result.url]
        }));
      }
    });
  };

  const handleSaveDiamond = async () => {
    try {
      const data = {
        ...diamondForm,
        size: parseFloat(diamondForm.size),
        rap_discount: parseFloat(diamondForm.rap_discount),
        price_per_carat: parseFloat(diamondForm.price_per_carat),
        total_amount: parseFloat(diamondForm.total_amount),
      };

      if (editingDiamond) {
        await api.updateDiamond(editingDiamond.diamond_id, data);
        toast.success('Diamond updated successfully');
      } else {
        await api.createDiamond(data);
        toast.success('Diamond created successfully');
      }

      setShowDiamondForm(false);
      setEditingDiamond(null);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error('Failed to save diamond');
    }
  };

  const handleDeleteDiamond = async (diamondId) => {
    if (!window.confirm('Are you sure you want to delete this diamond?')) return;
    
    try {
      await api.deleteDiamond(diamondId);
      toast.success('Diamond deleted successfully');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete diamond');
    }
  };

  const handleEditDiamond = (diamond) => {
    setEditingDiamond(diamond);
    setDiamondForm(diamond);
    setShowDiamondForm(true);
  };

  const resetForm = () => {
    setDiamondForm({
      shape: '',
      size: '',
      colour: '',
      clarity: '',
      measurement: '',
      fluorescence: '',
      certificate: '',
      lab: '',
      cut: '',
      polish: '',
      symmetry: '',
      comment: '',
      no_bgm: false,
      eye_clean: false,
      location: '',
      rap_discount: '',
      price_per_carat: '',
      total_amount: '',
      visibility: 'public',
      ratio: '',
      stock_id: '',
      shade: '',
      images: [],
      videos: [],
    });
  };

  return (
    <div className="min-h-screen px-6 md:px-12 lg:px-24 py-12">
      <div className="mb-12" data-testid="admin-dashboard-header">
        <h1 className="font-heading text-4xl md:text-5xl font-semibold mb-4">Admin Dashboard</h1>
        <p className="text-muted-foreground">Manage users and diamond inventory</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-4 mb-8 border-b border-border" data-testid="admin-tabs">
        <button
          onClick={() => setActiveTab('users')}
          className={`pb-4 px-6 font-body uppercase tracking-widest text-xs font-bold transition-colors ${
            activeTab === 'users'
              ? 'border-b-2 border-primary text-primary'
              : 'text-muted-foreground hover:text-primary'
          }`}
          data-testid="users-tab"
        >
          <Users className="inline mr-2" size={16} />
          Users
        </button>
        <button
          onClick={() => setActiveTab('diamonds')}
          className={`pb-4 px-6 font-body uppercase tracking-widest text-xs font-bold transition-colors ${
            activeTab === 'diamonds'
              ? 'border-b-2 border-primary text-primary'
              : 'text-muted-foreground hover:text-primary'
          }`}
          data-testid="diamonds-tab"
        >
          <Gem className="inline mr-2" size={16} />
          Diamonds
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-24">
          <DiamondLoader size="lg" />
        </div>
      ) : (
        <>
          {activeTab === 'users' ? (
            <div className="space-y-12">
              {/* Pending Approvals */}
              <div data-testid="pending-users-section">
                <h2 className="font-heading text-2xl font-semibold mb-6">Pending Approvals ({pendingUsers.length})</h2>
                {pendingUsers.length === 0 ? (
                  <p className="text-muted-foreground">No pending user approvals</p>
                ) : (
                  <div className="space-y-4">
                    {pendingUsers.map(user => (
                      <div key={user.user_id} className="bg-white border border-border p-6" data-testid={`pending-user-${user.user_id}`}>
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
                          <div>
                            <span className="text-xs uppercase tracking-widest text-muted-foreground">Name</span>
                            <p className="font-semibold">{user.first_name} {user.last_name}</p>
                          </div>
                          <div>
                            <span className="text-xs uppercase tracking-widest text-muted-foreground">Company</span>
                            <p className="font-semibold">{user.company_name}</p>
                          </div>
                          <div>
                            <span className="text-xs uppercase tracking-widest text-muted-foreground">Email</span>
                            <p className="text-sm">{user.email}</p>
                          </div>
                          <div>
                            <span className="text-xs uppercase tracking-widest text-muted-foreground">Contact</span>
                            <p className="text-sm">{user.contact_number}</p>
                          </div>
                        </div>
                        <div className="flex gap-3">
                          <button
                            onClick={() => handleApproveUser(user.user_id)}
                            className="bg-primary text-white hover:bg-primary/90 px-6 py-2 text-xs uppercase tracking-widest font-bold transition-colors flex items-center gap-2"
                            data-testid={`approve-user-${user.user_id}`}
                          >
                            <Check size={14} />
                            Approve
                          </button>
                          <button
                            onClick={() => handleRejectUser(user.user_id)}
                            className="bg-transparent border border-destructive text-destructive hover:bg-destructive hover:text-white px-6 py-2 text-xs uppercase tracking-widest font-bold transition-colors flex items-center gap-2"
                            data-testid={`reject-user-${user.user_id}`}
                          >
                            <X size={14} />
                            Reject
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* All Users */}
              <div data-testid="all-users-section">
                <h2 className="font-heading text-2xl font-semibold mb-6">All Users ({allUsers.length})</h2>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-border text-left">
                        <th className="pb-4 text-xs uppercase tracking-widest text-muted-foreground">Name</th>
                        <th className="pb-4 text-xs uppercase tracking-widest text-muted-foreground">Company</th>
                        <th className="pb-4 text-xs uppercase tracking-widest text-muted-foreground">Email</th>
                        <th className="pb-4 text-xs uppercase tracking-widest text-muted-foreground">Status</th>
                        <th className="pb-4 text-xs uppercase tracking-widest text-muted-foreground">Role</th>
                        <th className="pb-4 text-xs uppercase tracking-widest text-muted-foreground">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {allUsers.map(user => (
                        <tr key={user.user_id} className="border-b border-border/50 hover:bg-surface transition-colors">
                          <td className="py-4">{user.first_name} {user.last_name}</td>
                          <td className="py-4">{user.company_name}</td>
                          <td className="py-4 text-sm">{user.email}</td>
                          <td className="py-4">
                            <span className={`px-3 py-1 text-xs uppercase tracking-widest font-bold ${
                              user.status === 'approved' ? 'bg-primary/10 text-primary' :
                              user.status === 'pending' ? 'bg-yellow-500/10 text-yellow-700' :
                              user.status === 'suspended' ? 'bg-red-500/10 text-red-700' :
                              'bg-gray-500/10 text-gray-700'
                            }`}>
                              {user.status}
                            </span>
                          </td>
                          <td className="py-4 text-sm uppercase">{user.role}</td>
                          <td className="py-4">
                            {user.role !== 'admin' && (
                              <div className="flex gap-2">
                                {user.status === 'suspended' ? (
                                  <button
                                    onClick={() => handleUnblockUser(user.user_id)}
                                    className="text-primary hover:text-primary/70 transition-colors flex items-center gap-1 text-xs"
                                    title="Unblock User"
                                  >
                                    <CheckCircle size={16} />
                                    Unblock
                                  </button>
                                ) : user.status === 'approved' ? (
                                  <button
                                    onClick={() => handleBlockUser(user.user_id)}
                                    className="text-destructive hover:text-destructive/70 transition-colors flex items-center gap-1 text-xs"
                                    title="Block User"
                                  >
                                    <Ban size={16} />
                                    Block
                                  </button>
                                ) : null}
                              </div>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          ) : (
            <div>
              <div className="flex justify-between items-center mb-8">
                <h2 className="font-heading text-2xl font-semibold">Diamond Inventory ({diamonds.length})</h2>
                <button
                  onClick={() => {
                    resetForm();
                    setEditingDiamond(null);
                    setShowDiamondForm(true);
                  }}
                  className="bg-primary text-white hover:bg-primary/90 px-6 py-3 text-xs uppercase tracking-widest font-bold transition-colors flex items-center gap-2"
                  data-testid="add-diamond-btn"
                >
                  <Plus size={16} />
                  Add Diamond
                </button>
              </div>

              {showDiamondForm && (
                <div className="bg-white border border-border p-8 mb-8" data-testid="diamond-form">
                  <h3 className="font-heading text-xl font-semibold mb-6">
                    {editingDiamond ? 'Edit Diamond' : 'Add New Diamond'}
                  </h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                    <input
                      type="text"
                      placeholder="Stock ID *"
                      value={diamondForm.stock_id}
                      onChange={(e) => setDiamondForm({...diamondForm, stock_id: e.target.value})}
                      className="border-b border-border focus:border-primary outline-none py-2 transition-colors"
                      data-testid="diamond-stock-id-input"
                    />
                    <input
                      type="text"
                      placeholder="Shape *"
                      value={diamondForm.shape}
                      onChange={(e) => setDiamondForm({...diamondForm, shape: e.target.value})}
                      className="border-b border-border focus:border-primary outline-none py-2 transition-colors"
                      data-testid="diamond-shape-input"
                    />
                    <input
                      type="number"
                      step="0.01"
                      placeholder="Size (Carat) *"
                      value={diamondForm.size}
                      onChange={(e) => setDiamondForm({...diamondForm, size: e.target.value})}
                      className="border-b border-border focus:border-primary outline-none py-2 transition-colors"
                      data-testid="diamond-size-input"
                    />
                    <input
                      type="text"
                      placeholder="Colour *"
                      value={diamondForm.colour}
                      onChange={(e) => setDiamondForm({...diamondForm, colour: e.target.value})}
                      className="border-b border-border focus:border-primary outline-none py-2 transition-colors"
                      data-testid="diamond-colour-input"
                    />
                    <input
                      type="text"
                      placeholder="Clarity *"
                      value={diamondForm.clarity}
                      onChange={(e) => setDiamondForm({...diamondForm, clarity: e.target.value})}
                      className="border-b border-border focus:border-primary outline-none py-2 transition-colors"
                    />
                    <input
                      type="text"
                      placeholder="Cut *"
                      value={diamondForm.cut}
                      onChange={(e) => setDiamondForm({...diamondForm, cut: e.target.value})}
                      className="border-b border-border focus:border-primary outline-none py-2 transition-colors"
                    />
                    <input
                      type="text"
                      placeholder="Polish *"
                      value={diamondForm.polish}
                      onChange={(e) => setDiamondForm({...diamondForm, polish: e.target.value})}
                      className="border-b border-border focus:border-primary outline-none py-2 transition-colors"
                    />
                    <input
                      type="text"
                      placeholder="Symmetry *"
                      value={diamondForm.symmetry}
                      onChange={(e) => setDiamondForm({...diamondForm, symmetry: e.target.value})}
                      className="border-b border-border focus:border-primary outline-none py-2 transition-colors"
                    />
                    <input
                      type="text"
                      placeholder="Lab *"
                      value={diamondForm.lab}
                      onChange={(e) => setDiamondForm({...diamondForm, lab: e.target.value})}
                      className="border-b border-border focus:border-primary outline-none py-2 transition-colors"
                    />
                    <input
                      type="text"
                      placeholder="Certificate *"
                      value={diamondForm.certificate}
                      onChange={(e) => setDiamondForm({...diamondForm, certificate: e.target.value})}
                      className="border-b border-border focus:border-primary outline-none py-2 transition-colors"
                    />
                    <input
                      type="text"
                      placeholder="Measurement"
                      value={diamondForm.measurement}
                      onChange={(e) => setDiamondForm({...diamondForm, measurement: e.target.value})}
                      className="border-b border-border focus:border-primary outline-none py-2 transition-colors"
                    />
                    <input
                      type="text"
                      placeholder="Fluorescence"
                      value={diamondForm.fluorescence}
                      onChange={(e) => setDiamondForm({...diamondForm, fluorescence: e.target.value})}
                      className="border-b border-border focus:border-primary outline-none py-2 transition-colors"
                    />
                    <input
                      type="text"
                      placeholder="Location *"
                      value={diamondForm.location}
                      onChange={(e) => setDiamondForm({...diamondForm, location: e.target.value})}
                      className="border-b border-border focus:border-primary outline-none py-2 transition-colors"
                    />
                    <input
                      type="text"
                      placeholder="Ratio"
                      value={diamondForm.ratio}
                      onChange={(e) => setDiamondForm({...diamondForm, ratio: e.target.value})}
                      className="border-b border-border focus:border-primary outline-none py-2 transition-colors"
                    />
                    <input
                      type="text"
                      placeholder="Shade"
                      value={diamondForm.shade}
                      onChange={(e) => setDiamondForm({...diamondForm, shade: e.target.value})}
                      className="border-b border-border focus:border-primary outline-none py-2 transition-colors"
                    />
                    <input
                      type="number"
                      step="0.01"
                      placeholder="Rap Discount *"
                      value={diamondForm.rap_discount}
                      onChange={(e) => setDiamondForm({...diamondForm, rap_discount: e.target.value})}
                      className="border-b border-border focus:border-primary outline-none py-2 transition-colors"
                    />
                    <input
                      type="number"
                      step="0.01"
                      placeholder="Price per Carat *"
                      value={diamondForm.price_per_carat}
                      onChange={(e) => setDiamondForm({...diamondForm, price_per_carat: e.target.value})}
                      className="border-b border-border focus:border-primary outline-none py-2 transition-colors"
                    />
                    <input
                      type="number"
                      step="0.01"
                      placeholder="Total Amount *"
                      value={diamondForm.total_amount}
                      onChange={(e) => setDiamondForm({...diamondForm, total_amount: e.target.value})}
                      className="border-b border-border focus:border-primary outline-none py-2 transition-colors"
                    />
                  </div>

                  <div className="mb-6">
                    <textarea
                      placeholder="Comment"
                      value={diamondForm.comment}
                      onChange={(e) => setDiamondForm({...diamondForm, comment: e.target.value})}
                      rows={3}
                      className="w-full border border-border focus:border-primary outline-none p-3 transition-colors"
                    />
                  </div>

                  <div className="flex gap-6 mb-6">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={diamondForm.no_bgm}
                        onChange={(e) => setDiamondForm({...diamondForm, no_bgm: e.target.checked})}
                        className="w-4 h-4"
                      />
                      <span className="text-sm">No BGM</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={diamondForm.eye_clean}
                        onChange={(e) => setDiamondForm({...diamondForm, eye_clean: e.target.checked})}
                        className="w-4 h-4"
                      />
                      <span className="text-sm">Eye Clean</span>
                    </label>
                  </div>

                  <div className="mb-6">
                    <label className="block text-sm uppercase tracking-widest text-muted-foreground mb-3">
                      Visibility
                    </label>
                    <select
                      value={diamondForm.visibility}
                      onChange={(e) => setDiamondForm({...diamondForm, visibility: e.target.value})}
                      className="border border-border focus:border-primary outline-none p-3 transition-colors"
                    >
                      <option value="public">Public</option>
                      <option value="hidden">Hidden</option>
                    </select>
                  </div>

                  <div className="mb-6">
                    <label className="block text-sm uppercase tracking-widest text-muted-foreground mb-3">
                      Upload Images & Videos (All formats supported)
                    </label>
                    <input
                      type="file"
                      multiple
                      accept="image/*,video/*"
                      onChange={handleMediaUpload}
                      className="block w-full text-sm"
                    />
                    {(diamondForm.images.length > 0 || diamondForm.videos.length > 0) && (
                      <div className="mt-4 space-y-4">
                        {diamondForm.images.length > 0 && (
                          <div>
                            <p className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Images</p>
                            <div className="flex gap-2 flex-wrap">
                              {diamondForm.images.map((img, idx) => (
                                <div key={idx} className="relative w-20 h-20">
                                  <img src={IMGURL+img} alt="" className="w-full h-full object-cover border" />
                                  <button
                                    onClick={() => setDiamondForm(prev => ({
                                      ...prev,
                                      images: prev.images.filter((_, i) => i !== idx)
                                    }))}
                                    className="absolute -top-2 -right-2 bg-destructive text-white rounded-full w-6 h-6 flex items-center justify-center"
                                  >
                                    <X size={12} />
                                  </button>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        {diamondForm.videos.length > 0 && (
                          <div>
                            <p className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Videos</p>
                            <div className="flex gap-2 flex-wrap">
                              {diamondForm.videos.map((vid, idx) => (
                                <div key={idx} className="relative w-40 h-24">
                                  <video src={IMGURL+vid} className="w-full h-full border" />
                                  <button
                                    onClick={() => setDiamondForm(prev => ({
                                      ...prev,
                                      videos: prev.videos.filter((_, i) => i !== idx)
                                    }))}
                                    className="absolute -top-2 -right-2 bg-destructive text-white rounded-full w-6 h-6 flex items-center justify-center"
                                  >
                                    <X size={12} />
                                  </button>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="flex gap-4">
                    <button
                      onClick={handleSaveDiamond}
                      className="bg-primary text-white hover:bg-primary/90 px-8 py-3 text-xs uppercase tracking-widest font-bold transition-colors"
                      data-testid="save-diamond-btn"
                    >
                      {editingDiamond ? 'Update Diamond' : 'Create Diamond'}
                    </button>
                    <button
                      onClick={() => {
                        setShowDiamondForm(false);
                        setEditingDiamond(null);
                        resetForm();
                      }}
                      className="bg-transparent border border-border text-foreground hover:bg-surface px-8 py-3 text-xs uppercase tracking-widest font-bold transition-colors"
                      data-testid="cancel-diamond-btn"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}

              <div className="overflow-x-auto" data-testid="diamonds-table">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-border text-left">
                      <th className="pb-4 text-xs uppercase tracking-widest text-muted-foreground">Stock ID</th>
                      <th className="pb-4 text-xs uppercase tracking-widest text-muted-foreground">Shape</th>
                      <th className="pb-4 text-xs uppercase tracking-widest text-muted-foreground">Size</th>
                      <th className="pb-4 text-xs uppercase tracking-widest text-muted-foreground">Color</th>
                      <th className="pb-4 text-xs uppercase tracking-widest text-muted-foreground">Clarity</th>
                      <th className="pb-4 text-xs uppercase tracking-widest text-muted-foreground">Shade</th>
                      <th className="pb-4 text-xs uppercase tracking-widest text-muted-foreground">Price</th>
                      <th className="pb-4 text-xs uppercase tracking-widest text-muted-foreground">Visibility</th>
                      <th className="pb-4 text-xs uppercase tracking-widest text-muted-foreground">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {diamonds.map(diamond => (
                      <tr key={diamond.diamond_id} className="border-b border-border/50 hover:bg-surface transition-colors">
                        <td className="py-4 font-mono text-sm">{diamond.stock_id || '-'}</td>
                        <td className="py-4">{diamond.shape}</td>
                        <td className="py-4 font-mono tabular-nums">{diamond.size} ct</td>
                        <td className="py-4">{diamond.colour}</td>
                        <td className="py-4">{diamond.clarity}</td>
                        <td className="py-4">{diamond.shade || '-'}</td>
                        <td className="py-4 font-mono tabular-nums">${diamond.total_amount?.toLocaleString()}</td>
                        <td className="py-4">
                          <span className={`px-3 py-1 text-xs uppercase tracking-widest font-bold ${
                            diamond.visibility === 'public' ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground'
                          }`}>
                            {diamond.visibility}
                          </span>
                        </td>
                        <td className="py-4">
                          <div className="flex gap-2">
                            <button
                              onClick={() => handleEditDiamond(diamond)}
                              className="text-primary hover:text-primary/70 transition-colors"
                              data-testid={`edit-diamond-${diamond.diamond_id}`}
                            >
                              <Edit size={18} />
                            </button>
                            <button
                              onClick={() => handleDeleteDiamond(diamond.diamond_id)}
                              className="text-destructive hover:text-destructive/70 transition-colors"
                              data-testid={`delete-diamond-${diamond.diamond_id}`}
                            >
                              <Trash2 size={18} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};
